
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { Package, AlertTriangle, History, Plus, Minus } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface InventoryManagerProps {
  productId: string;
  currentStock: number;
}

const InventoryManager = ({ productId, currentStock }: InventoryManagerProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [adjustment, setAdjustment] = useState({
    quantity: 0,
    type: "manual",
    reason: "",
  });

  const isLowStock = currentStock <= 10;
  const isOutOfStock = currentStock === 0;

  // Fetch inventory logs
  const { data: inventoryLogs } = useQuery({
    queryKey: ['inventory-logs', productId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('inventory_logs')
        .select('*')
        .eq('product_id', productId)
        .order('created_at', { ascending: false })
        .limit(10);
      
      if (error) throw error;
      return data;
    },
  });

  // Fetch stock alert settings
  const { data: stockAlert } = useQuery({
    queryKey: ['stock-alert', productId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('stock_alerts')
        .select('*')
        .eq('product_id', productId)
        .single();
      
      if (error && error.code !== 'PGRST116') throw error;
      return data;
    },
  });

  // Update inventory mutation
  const updateInventoryMutation = useMutation({
    mutationFn: async (adjustmentData: typeof adjustment) => {
      const { error } = await supabase.rpc('update_inventory', {
        p_product_id: productId,
        p_quantity_change: adjustmentData.quantity,
        p_change_type: adjustmentData.type,
        p_reason: adjustmentData.reason || null
      });

      if (error) throw error;
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Inventory updated successfully",
      });
      queryClient.invalidateQueries({ queryKey: ['inventory-logs', productId] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
      setAdjustment({ quantity: 0, type: "manual", reason: "" });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update inventory",
        variant: "destructive",
      });
    },
  });

  // Update stock alert mutation
  const updateStockAlertMutation = useMutation({
    mutationFn: async (threshold: number) => {
      const { error } = await supabase
        .from('stock_alerts')
        .upsert({
          product_id: productId,
          threshold_quantity: threshold,
          is_active: true
        });

      if (error) throw error;
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Stock alert updated successfully",
      });
      queryClient.invalidateQueries({ queryKey: ['stock-alert', productId] });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update stock alert",
        variant: "destructive",
      });
    },
  });

  const handleAdjustment = () => {
    if (adjustment.quantity === 0) return;
    updateInventoryMutation.mutate(adjustment);
  };

  const handleStockAlertUpdate = (threshold: number) => {
    updateStockAlertMutation.mutate(threshold);
  };

  return (
    <div className="space-y-6">
      <Card className="admin-card border-admin-border">
        <CardHeader>
          <CardTitle className="text-admin-foreground flex items-center gap-2">
            <Package className="h-5 w-5" />
            Inventory Management
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            <div>
              <Label className="text-sm font-medium">Current Stock</Label>
              <div className="flex items-center gap-2">
                <span className="text-2xl font-bold">{currentStock}</span>
                {isOutOfStock && (
                  <Badge variant="destructive" className="flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    Out of Stock
                  </Badge>
                )}
                {isLowStock && !isOutOfStock && (
                  <Badge variant="secondary" className="flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    Low Stock
                  </Badge>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <Label>Adjust Inventory</Label>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div>
                <Label className="text-sm">Quantity Change</Label>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setAdjustment(prev => ({ ...prev, quantity: prev.quantity - 1 }))}
                    disabled={updateInventoryMutation.isPending}
                  >
                    <Minus className="h-3 w-3" />
                  </Button>
                  <Input
                    type="number"
                    value={adjustment.quantity}
                    onChange={(e) => setAdjustment({ ...adjustment, quantity: Number(e.target.value) })}
                    className="text-center"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setAdjustment(prev => ({ ...prev, quantity: prev.quantity + 1 }))}
                    disabled={updateInventoryMutation.isPending}
                  >
                    <Plus className="h-3 w-3" />
                  </Button>
                </div>
              </div>
              <div>
                <Label className="text-sm">Type</Label>
                <Select
                  value={adjustment.type}
                  onValueChange={(value) => setAdjustment({ ...adjustment, type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manual">Manual Adjustment</SelectItem>
                    <SelectItem value="restock">Restock</SelectItem>
                    <SelectItem value="return">Return</SelectItem>
                    <SelectItem value="damaged">Damaged/Lost</SelectItem>
                    <SelectItem value="sale">Sale</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm">Stock Alert Threshold</Label>
                <Input
                  type="number"
                  value={stockAlert?.threshold_quantity || 10}
                  onChange={(e) => handleStockAlertUpdate(Number(e.target.value))}
                  disabled={updateStockAlertMutation.isPending}
                />
              </div>
              <div className="self-end">
                <Button
                  onClick={handleAdjustment}
                  disabled={adjustment.quantity === 0 || updateInventoryMutation.isPending}
                  className="w-full"
                >
                  {updateInventoryMutation.isPending ? "Updating..." : "Update Stock"}
                </Button>
              </div>
            </div>
            <div>
              <Label className="text-sm">Reason (Optional)</Label>
              <Textarea
                value={adjustment.reason}
                onChange={(e) => setAdjustment({ ...adjustment, reason: e.target.value })}
                placeholder="Reason for inventory adjustment..."
                rows={2}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="admin-card border-admin-border">
        <CardHeader>
          <CardTitle className="text-admin-foreground flex items-center gap-2">
            <History className="h-5 w-5" />
            Inventory History
          </CardTitle>
        </CardHeader>
        <CardContent>
          {inventoryLogs && inventoryLogs.length > 0 ? (
            <div className="space-y-3">
              {inventoryLogs.map((log) => (
                <div key={log.id} className="flex items-center justify-between p-3 border border-admin-border rounded">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className={`font-medium ${log.quantity_change > 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {log.quantity_change > 0 ? '+' : ''}{log.quantity_change}
                      </span>
                      <Badge variant="outline">{log.change_type}</Badge>
                    </div>
                    <p className="text-sm text-admin-muted-foreground">
                      {log.previous_quantity} → {log.new_quantity}
                    </p>
                    {log.reason && (
                      <p className="text-sm text-admin-muted-foreground italic">
                        "{log.reason}"
                      </p>
                    )}
                  </div>
                  <div className="text-right text-sm text-admin-muted-foreground">
                    <p>{new Date(log.created_at).toLocaleDateString()}</p>
                    <p>System</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6 text-admin-muted-foreground">
              <History className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="text-admin-foreground">No inventory changes yet</p>
              <p className="text-sm">Inventory adjustments will appear here</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default InventoryManager;
